import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link, useSearchParams } from "react-router";
import { useOpenWizard } from "@/lib/wizard-navigation";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Pencil, Pause, Play, X } from "lucide-react";
import {
  StatusBadge,
  destinationLifecycleTone,
  formatJobStatus,
  jobStatusTone,
} from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  FREE_TRIAL_ATTENDEES_PER_STREAM,
  AUTO_STREAM_FREE_ATTENDEE_LIMIT,
  trialUsageLevel,
} from "@/lib/trial";
import type { Destination } from "@/lib/types";
import { cn } from "@/lib/cn";
import {
  mappingJsonForSave,
  parseMapping,
  StreamMappingFields,
  type MappingForm,
} from "@/features/streams/stream-mapping-fields";

const DETAIL_HEIGHT_KEY = "as-streams-detail-height";
const DETAIL_DEFAULT = 320;
const DETAIL_MIN = 180;
const DETAIL_MAX_RATIO = 0.7;

type StreamDetail = Destination & {
  mappingJson?: string | null;
  scheduleIntervalSeconds?: number | null;
};

function formatEpoch(seconds: number | null | undefined): string {
  if (seconds == null) return "Never";
  return new Date(seconds * 1000).toLocaleString();
}

function streamHealthTone(stream: Destination) {
  if (stream.lastErrorCode || stream.lastRunStatus === "failed")
    return "failed" as const;
  if (stream.lastRunStatus === "running" || stream.lastRunStatus === "queued") {
    return "syncing" as const;
  }
  if (stream.lastRunStatus === "succeeded_with_warnings")
    return "warning" as const;
  return destinationLifecycleTone(stream.lifecycle);
}

function streamHealthLabel(stream: Destination): string {
  if (stream.lastErrorCode || stream.lastRunStatus === "failed")
    return "Unhealthy";
  if (stream.lastRunStatus === "running") return "Syncing";
  if (stream.lastRunStatus === "queued") return "Queued";
  if (stream.lastRunStatus === "succeeded_with_warnings") return "Warning";
  if (stream.lifecycle === "active" && stream.lastRunStatus === "succeeded")
    return "Healthy";
  return stream.lifecycle;
}

function readStoredDetailHeight(): number {
  try {
    const raw = localStorage.getItem(DETAIL_HEIGHT_KEY);
    const n = raw ? Number(raw) : NaN;
    if (Number.isFinite(n) && n >= DETAIL_MIN) return n;
  } catch {
    /* ignore */
  }
  return DETAIL_DEFAULT;
}

export function StreamsPage() {
  const { orgId, canManage } = useOrg();
  const openWizard = useOpenWizard();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("stream");

  const [streams, setStreams] = useState<Destination[]>([]);
  const [billingStatus, setBillingStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<StreamDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mapping, setMapping] = useState<MappingForm>(parseMapping(null));
  const [scheduleMinutes, setScheduleMinutes] = useState("60");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailHeight, setDetailHeight] = useState(readStoredDetailHeight);
  const [q, setQ] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<Destination | null>(null);
  const [configHistory, setConfigHistory] = useState<
    Array<{
      id: string;
      actorUserId: string | null;
      snapshot: string;
      createdAt: number;
    }>
  >([]);

  const splitRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const detailHeightRef = useRef(detailHeight);

  useEffect(() => {
    detailHeightRef.current = detailHeight;
  }, [detailHeight]);

  async function refreshList() {
    const r = await api<{
      streams: Destination[];
      billing?: { status?: string | null };
    }>(`/api/accounts/${orgId}/dashboard`);
    setStreams(r.streams ?? []);
    setBillingStatus(r.billing?.status ?? null);
    return r.streams ?? [];
  }

  useEffect(() => {
    setLoading(true);
    void refreshList()
      .catch(() => setStreams([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDirty(false);
      setMessage(null);
      setError(null);
      return;
    }
    const summary = streams.find((s) => s.id === selectedId);
    if (!summary?.conferenceId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setMessage(null);
    setError(null);
    setConfigHistory([]);
    void api<{ stream: StreamDetail }>(
      `/api/accounts/${orgId}/conferences/${summary.conferenceId}/streams/${selectedId}`,
    )
      .then((r) => {
        const stream = { ...summary, ...r.stream };
        setDetail(stream);
        setMapping(parseMapping(stream.mappingJson));
        setScheduleMinutes(
          stream.scheduleIntervalSeconds
            ? String(
                Math.max(1, Math.round(stream.scheduleIntervalSeconds / 60)),
              )
            : "60",
        );
        setDirty(false);
      })
      .catch(() => {
        setDetail(summary);
        setMapping(parseMapping(summary.mappingJson));
        setDirty(false);
      })
      .finally(() => setDetailLoading(false));

    void api<{
      versions: Array<{
        id: string;
        actorUserId: string | null;
        snapshot: string;
        createdAt: number;
      }>;
    }>(`/api/accounts/${orgId}/streams/${selectedId}/history`)
      .then((r) => setConfigHistory(r.versions ?? []))
      .catch(() => setConfigHistory([]));
  }, [orgId, selectedId, streams]);

  function selectStream(id: string) {
    setSearchParams({ stream: id }, { replace: true });
  }

  function clearSelection() {
    setSearchParams({}, { replace: true });
  }

  async function patchLifecycle(stream: Destination, lifecycle: string) {
    if (!canManage || !stream.conferenceId) return;
    setBusy(true);
    setError(null);
    try {
      await api(
        `/api/accounts/${orgId}/conferences/${stream.conferenceId}/streams/${stream.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ lifecycle }),
        },
      );
      setMessage(
        lifecycle === "archived"
          ? "Stream archived."
          : `Stream marked ${lifecycle}.`,
      );
      setArchiveTarget(null);
      if (lifecycle === "archived" && selectedId === stream.id)
        clearSelection();
      await refreshList();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  const isTrialing = billingStatus === "trialing";

  function trialUsageLabel(stream: Destination): string | null {
    if (!isTrialing || stream.lifecycle === "archived") return null;
    const used = stream.trialAttendeesUsed ?? 0;
    const limit = stream.trialAttendeeLimit ?? FREE_TRIAL_ATTENDEES_PER_STREAM;
    return `${used} / ${limit}`;
  }

  async function runAction(streamId: string, action: "run" | "test") {
    if (!canManage) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/accounts/${orgId}/streams/${streamId}/${action}`, {
        method: "POST",
        body: "{}",
      });
      setMessage(action === "test" ? "Test sync queued." : "Sync run queued.");
      await refreshList();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveConfig(e: FormEvent) {
    e.preventDefault();
    if (!detail?.conferenceId || !canManage) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const minutes = Math.max(1, Number(scheduleMinutes) || 60);
      await api(
        `/api/accounts/${orgId}/conferences/${detail.conferenceId}/streams/${detail.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            mappingJson: mappingJsonForSave(mapping),
            scheduleIntervalSeconds: minutes * 60,
          }),
        },
      );
      setMessage("Configuration saved.");
      setDirty(false);
      await refreshList();
      try {
        const hist = await api<{
          versions: Array<{
            id: string;
            actorUserId: string | null;
            snapshot: string;
            createdAt: number;
          }>;
        }>(`/api/accounts/${orgId}/streams/${detail.id}/history`);
        setConfigHistory(hist.versions ?? []);
      } catch {
        /* keep prior history */
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function onResizePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    dragRef.current = { startY: e.clientY, startHeight: detailHeight };
  }

  function onResizePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const container = splitRef.current;
    const max = container
      ? Math.floor(container.getBoundingClientRect().height * DETAIL_MAX_RATIO)
      : 600;
    const delta = dragRef.current.startY - e.clientY;
    const next = Math.min(
      max,
      Math.max(DETAIL_MIN, dragRef.current.startHeight + delta),
    );
    setDetailHeight(next);
  }

  function onResizePointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    try {
      localStorage.setItem(DETAIL_HEIGHT_KEY, String(detailHeightRef.current));
    } catch {
      /* ignore */
    }
  }

  const filtered = streams.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      s.name.toLowerCase().includes(needle) ||
      (s.conferenceName ?? "").toLowerCase().includes(needle) ||
      s.destinationProvider.toLowerCase().includes(needle) ||
      s.lifecycle.toLowerCase().includes(needle)
    );
  });

  if (loading) return <PageSkeleton />;

  if (streams.length === 0) {
    return (
      <EmptyState
        title="No Sync Streams Yet"
        description="Create a sync stream to connect a Zoom event to a destination."
        actionLabel={canManage ? "New Sync Stream" : undefined}
        onAction={canManage ? () => openWizard() : undefined}
      />
    );
  }

  const selectedSummary = streams.find((s) => s.id === selectedId) ?? null;

  return (
    <div
      ref={splitRef}
      className="flex h-[calc(100dvh-5.5rem)] flex-col gap-4 md:h-[calc(100dvh-3rem)]"
    >
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-rs-ink">Sync Streams</h2>
          <p className="text-sm text-rs-ink-muted">
            Pause, resume, and configure registrant sync for each destination.
          </p>
        </div>
        {canManage ? (
          <Button onClick={() => openWizard()}>New Sync Stream</Button>
        ) : null}
      </div>

      <Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-rs-border px-5 py-3">
          <Input
            className="max-w-xs"
            placeholder="Search streams…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search Sync Streams"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <ul className="divide-y divide-rs-border">
            {filtered.map((stream) => {
              const selected = stream.id === selectedId;
              return (
                <li key={stream.id}>
                  <div
                    className={cn(
                      "flex flex-wrap items-center gap-3 px-5 py-3",
                      selected ? "bg-rs-accent-soft" : "hover:bg-rs-muted",
                    )}
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => selectStream(stream.id)}
                    >
                      <p className="truncate font-medium text-rs-ink">
                        {stream.name}
                      </p>
                      <p className="truncate text-sm text-rs-ink-muted">
                        {stream.conferenceName
                          ? `${stream.conferenceName} · ${stream.destinationProvider}`
                          : stream.destinationProvider}
                        {" · "}
                        Last run {formatEpoch(stream.lastRunAt)}
                      </p>
                    </button>
                    <StatusBadge tone={streamHealthTone(stream)}>
                      {streamHealthLabel(stream)}
                    </StatusBadge>
                    <StatusBadge
                      tone={destinationLifecycleTone(stream.lifecycle)}
                    >
                      {stream.lifecycle}
                    </StatusBadge>
                    {stream.origin === "auto" ? (
                      <StatusBadge tone="scheduled">Auto</StatusBadge>
                    ) : null}
                    {stream.origin === "auto" && stream.billingExempt ? (
                      <StatusBadge tone="ready">
                        Free (≤{AUTO_STREAM_FREE_ATTENDEE_LIMIT})
                      </StatusBadge>
                    ) : null}
                    {trialUsageLabel(stream) ? (
                      <span
                        className={cn(
                          "text-xs tabular-nums",
                          trialUsageLevel(stream.trialAttendeesUsed ?? 0) ===
                            "exhausted"
                            ? "font-medium text-rs-danger"
                            : trialUsageLevel(
                                  stream.trialAttendeesUsed ?? 0,
                                ) === "warning"
                              ? "font-medium text-rs-warning"
                              : "text-rs-ink-muted",
                        )}
                        title="Trial attendees used on this stream"
                      >
                        {trialUsageLabel(stream)} trial
                      </span>
                    ) : null}
                    {canManage ? (
                      <div className="flex items-center gap-1">
                        {stream.lifecycle === "active" ? (
                          <SimpleTooltip content="Pause Stream">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busy}
                              onClick={() =>
                                void patchLifecycle(stream, "paused")
                              }
                              aria-label="Pause Stream"
                            >
                              <Pause
                                className="h-4 w-4 text-rs-danger"
                                fill="currentColor"
                              />
                            </Button>
                          </SimpleTooltip>
                        ) : null}
                        {stream.lifecycle === "paused" ? (
                          <SimpleTooltip content="Resume Stream">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busy}
                              onClick={() =>
                                void patchLifecycle(stream, "active")
                              }
                              aria-label="Resume Stream"
                            >
                              <Play
                                className="h-4 w-4 text-rs-success"
                                fill="currentColor"
                              />
                            </Button>
                          </SimpleTooltip>
                        ) : null}
                        {stream.lifecycle === "paused" ? (
                          <SimpleTooltip content="Cancel Stream">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busy}
                              onClick={() => setArchiveTarget(stream)}
                              aria-label="Cancel Stream"
                            >
                              <X
                                className="h-4 w-4 text-rs-danger"
                                fill="currentColor"
                              />
                            </Button>
                          </SimpleTooltip>
                        ) : null}
                        <SimpleTooltip content="Edit Stream">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => selectStream(stream.id)}
                            aria-label="Edit Stream"
                          >
                            <Pencil
                              className="h-4 w-4 text-yellow-500"
                              fill="currentColor"
                            />
                          </Button>
                        </SimpleTooltip>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {filtered.length === 0 ? (
              <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                No streams match your search.
              </li>
            ) : null}
          </ul>
        </div>

        {selectedId && selectedSummary ? (
          <>
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize Detail Panel"
              tabIndex={0}
              className="flex h-3 shrink-0 cursor-row-resize items-center justify-center border-t border-rs-border bg-rs-muted/60 hover:bg-rs-muted"
              onPointerDown={onResizePointerDown}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
              onPointerCancel={onResizePointerUp}
            >
              <span
                className="h-1 w-10 rounded-full bg-rs-border"
                aria-hidden
              />
            </div>
            <div
              className="shrink-0 overflow-auto border-t border-rs-border bg-rs-elevated"
              style={{ height: detailHeight }}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-rs-border px-5 py-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-rs-ink">
                    {detail?.name ?? selectedSummary.name}
                  </h3>
                  <p className="text-sm text-rs-ink-muted">
                    {selectedSummary.conferenceName ? (
                      <>
                        <Link
                          to={`/conferences/${selectedSummary.conferenceId}`}
                          className="text-rs-info hover:underline"
                        >
                          {selectedSummary.conferenceName}
                        </Link>
                        {" · "}
                      </>
                    ) : null}
                    {selectedSummary.destinationProvider}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  Close
                </Button>
              </div>

              {detailLoading ? (
                <p className="px-5 py-6 text-sm text-rs-ink-muted">
                  Loading configuration…
                </p>
              ) : (
                <form className="space-y-5 p-5" onSubmit={saveConfig}>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <p className="text-xs font-medium text-rs-ink-muted">
                        Last Success
                      </p>
                      <p className="text-sm text-rs-ink">
                        {formatEpoch(selectedSummary.lastSuccessAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-rs-ink-muted">
                        Last Failure
                      </p>
                      <p className="text-sm text-rs-ink">
                        {formatEpoch(selectedSummary.lastFailureAt)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-rs-ink-muted">
                        Last Run
                      </p>
                      <p className="text-sm text-rs-ink">
                        {formatEpoch(selectedSummary.lastRunAt)}
                      </p>
                    </div>
                  </div>

                  {trialUsageLabel(selectedSummary) ? (
                    <div>
                      <p className="text-xs font-medium text-rs-ink-muted">
                        Trial Attendees
                      </p>
                      <p
                        className={cn(
                          "text-sm tabular-nums",
                          trialUsageLevel(
                            selectedSummary.trialAttendeesUsed ?? 0,
                          ) === "exhausted"
                            ? "font-medium text-rs-danger"
                            : trialUsageLevel(
                                  selectedSummary.trialAttendeesUsed ?? 0,
                                ) === "warning"
                              ? "font-medium text-rs-warning"
                              : "text-rs-ink",
                        )}
                      >
                        {trialUsageLabel(selectedSummary)} used
                      </p>
                      {trialUsageLevel(
                        selectedSummary.trialAttendeesUsed ?? 0,
                      ) === "exhausted" ? (
                        <p className="mt-1 text-xs text-rs-danger">
                          Trial allowance reached —{" "}
                          <Link
                            to="/settings/billing"
                            className="underline-offset-2 hover:underline"
                          >
                            upgrade billing
                          </Link>{" "}
                          to continue.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {(selectedSummary.recentRuns?.length ?? 0) > 0 ? (
                    <div>
                      <p className="mb-2 text-sm font-medium text-rs-ink">
                        Recent Runs
                      </p>
                      <ul className="space-y-1.5">
                        {selectedSummary.recentRuns!.slice(0, 5).map((run) => (
                          <li
                            key={run.id}
                            className="flex items-center justify-between gap-2 text-sm"
                          >
                            <span className="text-rs-ink-muted">
                              {formatEpoch(run.ranAt)}
                            </span>
                            <StatusBadge tone={jobStatusTone(run.status)}>
                              {formatJobStatus(run.status)}
                            </StatusBadge>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div>
                    <p className="mb-2 text-sm font-medium text-rs-ink">
                      Configuration History
                    </p>
                    {configHistory.length === 0 ? (
                      <p className="text-sm text-rs-ink-muted">
                        No saved configuration versions yet. Changes from Save
                        Changes appear here.
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {configHistory.slice(0, 10).map((version) => {
                          let summary = "Configuration updated";
                          try {
                            const snap = JSON.parse(version.snapshot) as Record<
                              string,
                              unknown
                            >;
                            const keys = Object.keys(snap).filter(
                              (k) => snap[k] != null,
                            );
                            if (keys.length)
                              summary = `Updated: ${keys.slice(0, 4).join(", ")}`;
                          } catch {
                            /* keep default */
                          }
                          return (
                            <li
                              key={version.id}
                              className="flex flex-wrap items-center justify-between gap-2 text-sm"
                            >
                              <span className="text-rs-ink-muted">
                                {formatEpoch(version.createdAt)}
                              </span>
                              <span className="text-rs-ink">{summary}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <StreamMappingFields
                    mapping={mapping}
                    scheduleMinutes={scheduleMinutes}
                    canManage={canManage}
                    idPrefix="stream"
                    onMappingChange={(next) => {
                      setMapping(next);
                      setDirty(true);
                    }}
                    onScheduleMinutesChange={(next) => {
                      setScheduleMinutes(next);
                      setDirty(true);
                    }}
                  />

                  {canManage ? (
                    <div className="flex flex-wrap gap-2">
                      <Button type="submit" disabled={!dirty || busy}>
                        {busy ? "Saving…" : "Save Changes"}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          void runAction(selectedSummary.id, "test")
                        }
                      >
                        Run Test Sync
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={
                          busy ||
                          !["active", "tested"].includes(
                            selectedSummary.lifecycle,
                          )
                        }
                        onClick={() =>
                          void runAction(selectedSummary.id, "run")
                        }
                      >
                        Run Sync
                      </Button>
                      {selectedSummary.lifecycle === "active" ? (
                        <SimpleTooltip content="Pause Stream">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={busy}
                            onClick={() =>
                              void patchLifecycle(selectedSummary, "paused")
                            }
                            aria-label="Pause Stream"
                          >
                            <Pause
                              className="h-4 w-4 text-rs-danger"
                              fill="currentColor"
                            />
                          </Button>
                        </SimpleTooltip>
                      ) : null}
                      {selectedSummary.lifecycle === "paused" ? (
                        <SimpleTooltip content="Resume Stream">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={busy}
                            onClick={() =>
                              void patchLifecycle(selectedSummary, "active")
                            }
                            aria-label="Resume Stream"
                          >
                            <Play
                              className="h-4 w-4 text-rs-success"
                              fill="currentColor"
                            />
                          </Button>
                        </SimpleTooltip>
                      ) : null}
                      {selectedSummary.lifecycle === "paused" ? (
                        <SimpleTooltip content="Cancel Stream">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={busy}
                            onClick={() => setArchiveTarget(selectedSummary)}
                            aria-label="Cancel Stream"
                          >
                            <X
                              className="h-4 w-4 text-rs-danger"
                              fill="currentColor"
                            />
                          </Button>
                        </SimpleTooltip>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-sm text-rs-ink-muted">
                      View only — ask an admin to change configuration.
                    </p>
                  )}
                  {message ? (
                    <p className="text-sm text-rs-success">{message}</p>
                  ) : null}
                  {error ? (
                    <p className="text-sm text-rs-danger">{error}</p>
                  ) : null}
                </form>
              )}
            </div>
          </>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={archiveTarget != null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
        title="Archive Sync Stream"
        description="Archived streams stop syncing and no longer count toward active stream billing. You can restore a stream later by setting it back to draft."
        impact={
          archiveTarget ? (
            <p>
              Archive <span className="font-medium">{archiveTarget.name}</span>?
            </p>
          ) : null
        }
        confirmLabel="Archive Stream"
        dangerous
        busy={busy}
        onConfirm={() => {
          if (archiveTarget) return patchLifecycle(archiveTarget, "archived");
        }}
      />
    </div>
  );
}
